/**
 * OrcaAccountPage - Comprehensive account management
 * Apple-style design matching FMA Home aesthetic with mobile-first approach
 */
import { useState, useEffect, Suspense, lazy } from "react";
import { useAuth } from "@/global/auth/AuthContext";
import { supabase } from "@/lib/supabase";
import { callFunction } from "@/lib/http";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/global/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Mail,
  Key,
  CreditCard,
  User,
  Loader2,
  Check,
  AlertCircle,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ShootingStars } from "@/components/ui/shooting-stars";
import { StarsBackground } from "@/components/ui/stars-background";

// Lazy load EnhancedBillingManagement to avoid circular dependencies
const EnhancedBillingManagement = lazy(
  () =>
    import("../../srcFMA/pages/Account/components/EnhancedBillingManagement")
);

export default function OrcaAccountPage() {
  const { user, activeTenantId } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Email change state
  const [newEmail, setNewEmail] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Payment method state
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Add page class for background override
  useEffect(() => {
    document.body.classList.add("orca-account-page");
    return () => {
      document.body.classList.remove("orca-account-page");
    };
  }, []);

  // Get user's first name for personalized greeting
  const firstName = user?.user_metadata?.first_name || user?.email?.split("@")[0] || "there";

  const handleEmailChange = async () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });

      if (error) throw error;

      toast({
        title: "Email update initiated",
        description: "Check both your old and new email for confirmation links",
      });

      setEmailDialogOpen(false);
      setNewEmail("");
    } catch (error: any) {
      toast({
        title: "Failed to update email",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: "All fields required",
        description: "Please fill in all password fields",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "New password and confirmation must match",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    setPasswordLoading(true);
    try {
      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || "",
        password: currentPassword,
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been changed successfully",
      });

      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast({
        title: "Failed to update password",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleManagePaymentMethods = async () => {
    if (!activeTenantId) {
      toast({
        title: "No active organization",
        description: "Please select an organization first",
        variant: "destructive",
      });
      return;
    }

    setPaymentLoading(true);
    try {
      const result = await callFunction("billing_create-portal-session", {
        organization_id: activeTenantId,
        return_url: window.location.href,
      });

      if (result && typeof result === "object" && "url" in result) {
        // Redirect to Stripe Customer Portal
        window.location.href = result.url as string;
      } else {
        throw new Error("Failed to get portal URL");
      }
    } catch (error: any) {
      toast({
        title: "Failed to open payment portal",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
      setPaymentLoading(false);
    }
  };

  return (
    <>
      {/* CSS override for layout background - inline styles for immediate effect */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .orca-account-page body,
          .orca-account-page .min-h-screen.bg-background {
            background: transparent !important;
          }
        `,
        }}
      />

      {/* Animated background with shooting stars */}
      <div className="fixed inset-0 bg-neutral-900 z-0"></div>
      <StarsBackground
        starDensity={0.0003}
        className="fixed inset-0 z-10"
      />
      <ShootingStars
        minSpeed={10}
        maxSpeed={30}
        minDelay={800}
        maxDelay={3000}
        starColor="#ff6b35"
        trailColor="#f97316"
        starWidth={15}
        starHeight={2}
        className="fixed inset-0 z-20"
      />

      {/* Content */}
      <div className="relative z-30 min-h-screen w-full max-w-none px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8">
        {/* Back button */}
        <div className="max-w-6xl mx-auto mb-4 sm:mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Hero Section */}
        <div className="max-w-6xl mx-auto text-center space-y-3 sm:space-y-4 mb-6 sm:mb-8 px-2 sm:px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Account Settings
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto">
            Manage your Orca account, billing, and security preferences
          </p>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
          {/* Profile & Security Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Email Card */}
            <Card className="bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-200 rounded-lg sm:rounded-xl">
              <CardHeader className="pb-3 sm:pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl text-white">
                    Email Address
                  </CardTitle>
                </div>
                <CardDescription className="text-sm sm:text-base text-white/60">
                  Update your email for account notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                <div className="bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                  <div className="text-xs sm:text-sm text-white/50 mb-1">
                    Current Email
                  </div>
                  <div className="text-sm sm:text-base text-white font-medium break-all">
                    {user?.email || "Not set"}
                  </div>
                </div>

                <Dialog
                  open={emailDialogOpen}
                  onOpenChange={setEmailDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base py-2 sm:py-3"
                    >
                      Change Email
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900/95 backdrop-blur-xl border border-white/20 text-white max-w-[95vw] sm:max-w-md mx-2 sm:mx-auto">
                    <DialogHeader>
                      <DialogTitle className="text-lg sm:text-xl">
                        Change Email Address
                      </DialogTitle>
                      <DialogDescription className="text-sm sm:text-base text-white/70">
                        You'll receive confirmation emails at both addresses
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label
                          htmlFor="new-email"
                          className="text-white text-sm sm:text-base"
                        >
                          New Email Address
                        </Label>
                        <Input
                          id="new-email"
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="new@example.com"
                          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 mt-2 text-sm sm:text-base"
                        />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button
                          onClick={() => setEmailDialogOpen(false)}
                          variant="outline"
                          className="flex-1 bg-white/5 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base"
                          disabled={emailLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleEmailChange}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm sm:text-base"
                          disabled={emailLoading}
                        >
                          {emailLoading && (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          )}
                          Update Email
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>

            {/* Password Card */}
            <Card className="bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-200 rounded-lg sm:rounded-xl">
              <CardHeader className="pb-3 sm:pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                    <Key className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl text-white">
                    Password
                  </CardTitle>
                </div>
                <CardDescription className="text-sm sm:text-base text-white/60">
                  Change your account password
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4">
                <div className="bg-white/5 rounded-lg p-3 sm:p-4 border border-white/10">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                    <div className="text-sm sm:text-base text-white">
                      Password is set
                    </div>
                  </div>
                </div>

                <Dialog
                  open={passwordDialogOpen}
                  onOpenChange={setPasswordDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base py-2 sm:py-3"
                    >
                      Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900/95 backdrop-blur-xl border border-white/20 text-white max-w-[95vw] sm:max-w-md mx-2 sm:mx-auto">
                    <DialogHeader>
                      <DialogTitle className="text-lg sm:text-xl">
                        Change Password
                      </DialogTitle>
                      <DialogDescription className="text-sm sm:text-base text-white/70">
                        Enter your current password and choose a new one
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label
                          htmlFor="current-password"
                          className="text-white text-sm sm:text-base"
                        >
                          Current Password
                        </Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="bg-white/5 border-white/20 text-white mt-2 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="new-password"
                          className="text-white text-sm sm:text-base"
                        >
                          New Password
                        </Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="bg-white/5 border-white/20 text-white mt-2 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="confirm-password"
                          className="text-white text-sm sm:text-base"
                        >
                          Confirm New Password
                        </Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="bg-white/5 border-white/20 text-white mt-2 text-sm sm:text-base"
                        />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button
                          onClick={() => {
                            setPasswordDialogOpen(false);
                            setCurrentPassword("");
                            setNewPassword("");
                            setConfirmPassword("");
                          }}
                          variant="outline"
                          className="flex-1 bg-white/5 border-white/20 text-white hover:bg-white/10 text-sm sm:text-base"
                          disabled={passwordLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handlePasswordChange}
                          className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm sm:text-base"
                          disabled={passwordLoading}
                        >
                          {passwordLoading && (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          )}
                          Update Password
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>

          {/* Payment Methods Card */}
          <Card className="bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-200 rounded-lg sm:rounded-xl">
            <CardHeader className="pb-3 sm:pb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <CardTitle className="text-lg sm:text-xl text-white">
                  Payment Methods
                </CardTitle>
              </div>
              <CardDescription className="text-sm sm:text-base text-white/60">
                Manage your payment methods and billing information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4">
              <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg p-3 sm:p-4 border border-purple-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm sm:text-base text-white font-medium mb-1">
                      Secure Payment Portal
                    </div>
                    <p className="text-xs sm:text-sm text-white/70">
                      Manage your payment methods securely through Stripe's
                      Customer Portal. You can add, update, or remove payment
                      methods.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleManagePaymentMethods}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-medium text-sm sm:text-base py-2 sm:py-3"
                disabled={paymentLoading}
              >
                {paymentLoading ? (
                  <Loader2 className="h-4 w-4 sm:h-5 sm:h-5 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4 sm:h-5 sm:h-5 mr-2" />
                )}
                Open Payment Portal
              </Button>
            </CardContent>
          </Card>

          {/* Billing Management Section */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-3 px-2 sm:px-0">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" />
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                Subscription Management
              </h2>
            </div>
            <Suspense
              fallback={
                <Card className="bg-white/5 backdrop-blur-md border border-white/10">
                  <CardContent className="flex items-center justify-center p-12">
                    <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                  </CardContent>
                </Card>
              }
            >
              <EnhancedBillingManagement />
            </Suspense>
          </div>
        </div>

        {/* Footer with Terms & Conditions link */}
        <div className="mt-12 pb-8 text-center">
          <a
            href="/terms"
            className="text-xs sm:text-sm text-white/50 hover:text-white/70 transition-colors underline"
          >
            Terms & Conditions
          </a>
        </div>
      </div>
    </>
  );
}
